import { StyledIcon } from 'fitify-ui/src/Icon/Icon.Styled'
import { css, styled } from 'styled-components'

import { Typography } from '../../Typography'

import type {
  InfoCardAlign,
  InfoCardProps,
  InfoCardVariant,
} from './InfoCard.Types'

export const StyledContainer = styled.div<{
  $variant: InfoCardVariant
  $showBackground: boolean
  $align: InfoCardAlign
}>`
  display: flex;
  width: 100%;
  align-items: center;
  padding: ${({ $showBackground }) =>
    $showBackground ? '0.75rem' : '0.25rem'};
  border-radius: ${({ theme }) => theme.radius['Radius LG']}px;
  gap: 0.5rem;

  ${({ $align }) => {
    if ($align === 'center') {
      return css`
        flex-direction: column;
        text-align: center;
      `
    }

    return css`
      flex-direction: row;
    `
  }}

  ${({ $variant, $showBackground, theme }) => {
    if (!$showBackground) {
      return undefined
    }

    const backgroundColorMap: Record<InfoCardVariant, string> = {
      grey: theme.colors['N Surface/Secondary'],
      green: theme.colors['N Surface/Success'],
      blue: theme.colors['N Surface/Good'],
      orange: theme.colors['N Surface/Warning'],
      red: theme.colors['N Surface/Error'],
      brand: theme.colors['N Surface/Brand'],
      white: theme.colors['N Surface/Primary'],
    }

    return css`
      backdrop-filter: blur(0.75rem);
      background-color: ${backgroundColorMap[$variant]};
    `
  }}
`

export const StyledIconWrapper = styled.div<{
  $iconColor?: InfoCardProps['iconColor']
  $variant: InfoCardVariant
}>`
  display: flex;
  flex-shrink: 0;

  ${StyledIcon} {
    width: 1.5rem;
    height: 1.5rem;

    color: ${({ $iconColor, $variant, theme }) => {
      if ($iconColor) {
        return theme.colors[$iconColor]
      }
      const iconColorMap: Record<InfoCardVariant, string> = {
        grey: theme.colors['N Content/Secondary'],
        green: theme.colors['N General/Success'],
        blue: theme.colors['N General/Good'],
        orange: theme.colors['N General/Warning'],
        red: theme.colors['N General/Error'],
        brand: theme.colors['N Content/Highlighted'],
        white: theme.colors['N Content/Secondary'],
      }
      return iconColorMap[$variant]
    }};
  }
`

export const StyledContent = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.25rem;
`

export const StyledHeadingContainer = styled.div<{ $align: InfoCardAlign }>`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === 'center' ? 'center' : 'flex-start'};
  gap: 0.5rem;
`

export const StyledBody = styled(Typography)<{
  $showBackground: boolean
  $colorVariant: InfoCardVariant
}>`
  color: ${({ $showBackground, theme, $colorVariant }) => {
    if ($showBackground) {
      if ($colorVariant === 'grey' || $colorVariant === 'white') {
        return theme.colors['N Content/Secondary']
      }
      return theme.colors['N Content/Primary']
    }

    return theme.colors['N Content/Secondary']
  }};
  overflow-wrap: anywhere;
  a {
    color: inherit;
    text-decoration: underline;
  }
  b {
    color: ${({ $colorVariant, theme }) => {
      const iconColorMap: Record<InfoCardVariant, string> = {
        grey: theme.colors['N Content/Tertiary'],
        green: theme.colors['N General/Success'],
        blue: theme.colors['N General/Good'],
        orange: theme.colors['N General/Warning'],
        red: theme.colors['N General/Error'],
        brand: theme.colors['N Content/Highlighted'],
        white: theme.colors['N Content/Tertiary'],
      }
      return iconColorMap[$colorVariant]
    }};
    font-weight: 500;
  }
`
